import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

// Custom Modal components
const ModalOverlay = styled.div`
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => (theme.name === 'dark' ? '#1e293b' : '#ffffff')};
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: calc(100% - 32px);
  max-height: calc(100vh - 64px);
  overflow: auto;
`;

const ConfirmationContent = styled.div`
  text-align: center;
  padding: 32px 24px;
`;

const ConfirmationIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(250, 70, 22, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #fa4616;
`;

const ConfirmationTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const ConfirmationText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 0 0 28px 0;
  line-height: 1.6;

  strong {
    color: ${({ theme }) => (theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  }
`;

const ConfirmationActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ActionButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;

  ${({ variant, theme }) =>
    variant === 'primary'
      ? `
    background: linear-gradient(135deg, #fa4616, #ff6b3b);
    color: #ffffff;
    border: none;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(250, 70, 22, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `
      : `
    background: transparent;
    color: ${theme.name === 'dark' ? '#94a3b8' : '#64748b'};
    border: 1px solid ${
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#e2e8f0'
    };

    &:hover:not(:disabled) {
      background: ${
        theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.05)'
      };
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `}
`;

const LoginIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

const LoginAsConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  memberName,
  isLoggingIn,
}) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ConfirmationContent>
          <ConfirmationIcon>
            <LoginIcon />
          </ConfirmationIcon>
          <ConfirmationTitle>Login as User?</ConfirmationTitle>
          <ConfirmationText>
            Are you sure you want to login as{' '}
            <strong>{memberName || 'this user'}</strong>? You will be logged out
            of your current session.
          </ConfirmationText>
          <ConfirmationActions>
            <ActionButton
              type="button"
              onClick={onClose}
              disabled={isLoggingIn}
            >
              Cancel
            </ActionButton>
            <ActionButton
              type="button"
              variant="primary"
              onClick={onConfirm}
              disabled={isLoggingIn}
            >
              {isLoggingIn ? 'Logging in...' : 'Login'}
            </ActionButton>
          </ConfirmationActions>
        </ConfirmationContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

LoginAsConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  memberName: PropTypes.string,
  isLoggingIn: PropTypes.bool,
};

export default LoginAsConfirmationModal;
